/** Multi-event tier helpers shared by the bulk event list and the Confirm
 *  upsell. Mirrors discount_service.events_bulk_line_discount: of the tiers
 *  the batch size qualifies for, the one worth the most on a line wins. */
import type { BulkOffer } from '@/api/embed';
import { money } from './bulkFormat';

export function offerDiscount(offer: BulkOffer, feeCents: number): number {
  if (offer.discount_kind === 'PERCENT') {
    return Math.round((feeCents * (offer.percent_off ?? 0)) / 100);
  }
  return Math.min(feeCents, offer.amount_off_cents ?? 0);
}

/** "10%" or "$50" */
export function offerLabel(offer: BulkOffer): string {
  if (offer.discount_kind === 'PERCENT') return `${offer.percent_off ?? 0}%`;
  const cents = offer.amount_off_cents ?? 0;
  return cents % 100 === 0 ? `$${(cents / 100).toLocaleString('en-US')}` : money(cents);
}

// Stands in for an unknown or $0 price when ranking tiers, so a qualifying
// tier is still found (a percent of $0 would rank every tier at nothing).
const NOMINAL_FEE_CENTS = 10000;

/** Best tier a batch of `count` events qualifies for, judged on `feeCents`. */
export function bestOffer(offers: BulkOffer[], count: number, feeCents: number): BulkOffer | null {
  const fee = feeCents > 0 ? feeCents : NOMINAL_FEE_CENTS;
  let best: BulkOffer | null = null;
  let bestCents = 0;
  for (const o of offers) {
    if (count < o.min_events) continue;
    const d = offerDiscount(o, fee);
    if (d > bestCents) {
      best = o;
      bestCents = d;
    }
  }
  return best;
}

/** The next threshold above `count` that would be worth more than `current`. */
export function nextOffer(
  offers: BulkOffer[],
  count: number,
  feeCents: number,
  current: BulkOffer | null,
): BulkOffer | null {
  const fee = feeCents > 0 ? feeCents : NOMINAL_FEE_CENTS;
  const now = current ? offerDiscount(current, fee) : 0;
  return (
    offers
      .filter((o) => o.min_events > count && offerDiscount(o, fee) > now)
      .sort((a, b) => a.min_events - b.min_events)[0] ?? null
  );
}

/** Whole percent a discount is of its fee, for the "−10%" tag. */
export function pctOff(discountCents: number, feeCents: number): number {
  return feeCents > 0 ? Math.round((discountCents / feeCents) * 100) : 0;
}

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}
